import cn from 'classnames'
import React from 'react'
import { Icon } from '../../icon'
import { IconCountryFlag } from '../../icon-country-flag'
import { Menu } from '../../menu'
import { Typography } from '../../typography'
import { BaseField, ValidationIcon } from '../base-field/BaseField'
import { Input } from '../base-field/input'
import styles from './PhoneField.module.css'
import { Country, PhoneFieldProps } from './PhoneField.types'

function PhoneField(
  {
    ref,
    countries,
    $disabled,
    value,
    countryCode,
    defaultCountryCode,
    status = 'default',
    onPhoneChange,
    onCountryChange,
    inputProps,
    name,
    onBlur,
    modal = false,
    ...rest
  }: PhoneFieldProps,
) {
  const [open, setOpen] = React.useState(false)
  const justOpenedRef = React.useRef(false)
  const [selectedCountry, setSelectedCountry] = React.useState<Country>(
    countries.find(({ code }) => code === defaultCountryCode) ?? countries[0],
  )

  const [isCountryChanging, setIsCountryChanging] = React.useState(false)
  const [visiblePhoneValue, setVisiblePhoneValue] = React.useState<string>(
    typeof value === 'string' ? value.replace(`+${selectedCountry.code} `, '').trim() : '',
  )

  React.useEffect(() => {
    if (!isCountryChanging) {
      return
    }
    const currentPhoneWithoutCode = visiblePhoneValue.trim()

    if (!currentPhoneWithoutCode) {
      return
    }

    onPhoneChange
    && onPhoneChange({
      phoneNumber: currentPhoneWithoutCode,
      countryCode: selectedCountry.code,
    })
    setIsCountryChanging(false)
  }, [selectedCountry, isCountryChanging, onPhoneChange, visiblePhoneValue])

  React.useEffect(() => {
    if (value) {
      setVisiblePhoneValue(value.replace(`+${selectedCountry.code} `, '').trim())
    }
  }, [value, selectedCountry])

  React.useEffect(() => {
    if (countryCode) {
      const newSelectedCountry = countries.find(({ code }) => code === countryCode)
      if (newSelectedCountry) {
        setSelectedCountry(newSelectedCountry)
      }
    }
  }, [countryCode, countries])

  const handleCountryClick = (country: Country) => {
    setIsCountryChanging(true)
    setSelectedCountry(country)
    onCountryChange && onCountryChange(country)
    setOpen(false)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPhoneValue = event.target.value
    setVisiblePhoneValue(newPhoneValue)
    if (!isCountryChanging) {
      onPhoneChange
      && onPhoneChange({
        phoneNumber: newPhoneValue,
        countryCode: selectedCountry.code,
      })
    }
  }

  return (
    <BaseField {...rest} status={status} className={cn(styles.baseField, rest.className)} name={name}>
      <Menu.Root
        modal={modal}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            justOpenedRef.current = true
            setTimeout(() => {
              justOpenedRef.current = false
            }, 300)
          }
          setOpen(isOpen)
        }}
        open={open}
        showMobileBackdrop
      >
        <Menu.Trigger data-country-selector data-testid="country-selector">
          <div className={styles.countrySelector} role="button" tabIndex={0}>
            <IconCountryFlag glyph={selectedCountry.icon} size={16} className={styles.iconCountryFlagWrapper} />
            <Typography variant="body-s-regular-tight" className={styles.countryCodeText}>
              +
              {selectedCountry.code}
            </Typography>
            <Icon glyph={open ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} size={16} className={styles.iconArrowWrapper} />
          </div>
        </Menu.Trigger>
        <Menu.Content
          position="bottom-right"
          className={cn(styles.overflowMenu, styles.mobileMenuContent)}
          wrapperClassName={cn(styles.mobileMenuWrapper, rest.wrapperClassName)}
          sideOffset={-4}
          scrollable={false}
        >
          <div
            className={styles.menuContent}
            onPointerUpCapture={(e) => {
              if (justOpenedRef.current) {
                e.stopPropagation()
                e.preventDefault()
              }
            }}
          >
            {countries?.map(item => (
              <Menu.Item
                key={item.isoCode}
                asChild={false}
                onSelect={() => handleCountryClick(item)}
                glyph={item.code === selectedCountry.code ? 'CheckMarkSmall' : undefined}
              >
                {item.name}
                {' '}
                (+
                {item.code}
                )
              </Menu.Item>
            ))}
          </div>
        </Menu.Content>
      </Menu.Root>
      <Input
        tabIndex={0}
        id={name}
        ref={ref}
        name={name}
        disabled={$disabled}
        value={visiblePhoneValue}
        type="tel"
        onChange={handleChange}
        onBlur={onBlur}
        {...inputProps}
        className={styles.phoneInput}
      />
      <ValidationIcon status={status} />
    </BaseField>
  )
}

export default PhoneField
